import { makeAutoObservable, runInAction } from 'mobx';
import { addOrUpdateDependent, removeSubscriberDependent, updateSubscriberInfo } from 'api/operations/electionService';
import { PersonInfo } from 'types/electionService';
import SubDependentDelegate from './dependents/subDependentDelegate';
import { mapSubscriberToBasicInfo, sortDependents } from './utils';
import { SubscriberBasicInfo, SubscriberPersonInfo } from './types';

export interface SubscriberBasicInfoDelegateParams {
  basicInfo: SubscriberBasicInfo;
  periodId: string;
}

/**
 * SSOT for subscriber primary profile and dependents only.
 * Does not query; data is supplied via constructor / {@link setBasicInfo}.
 * Prefer {@link fromSubscriber} when starting from a BE `Subscriber`.
 */
export default class SubscriberBasicInfoDelegate {
  private _basicInfo: SubscriberBasicInfo;
  private _periodId: string;
  private _dependentDelegates: SubDependentDelegate[] = [];

  /** Constructs a delegate from a BE `Subscriber` (maps at the entrance). */
  public static fromSubscriber(
    subscriber: Parameters<typeof mapSubscriberToBasicInfo>[0],
    periodId: string,
  ): SubscriberBasicInfoDelegate {
    return new SubscriberBasicInfoDelegate({
      basicInfo: mapSubscriberToBasicInfo(subscriber),
      periodId,
    });
  }

  public get basicInfo(): SubscriberBasicInfo {
    return this._basicInfo;
  }

  public get primary(): SubscriberPersonInfo {
    return this._basicInfo.primary;
  }

  public get subscriberId(): string {
    return this._basicInfo.id;
  }

  public get periodId(): string {
    return this._periodId;
  }

  public get subscriberDependentsDelegates(): SubDependentDelegate[] {
    return this._dependentDelegates;
  }

  private _getDependentDelegate(dependentId: string): SubDependentDelegate | undefined {
    return this._dependentDelegates.find((delegate) => delegate.dependent?.id === dependentId);
  }

  private _createDependentDelegates(): void {
    this._dependentDelegates = this._basicInfo.dependents.map(
      (dependent) =>
        new SubDependentDelegate({
          dependent,
          subscriberId: this._basicInfo.id,
        }),
    );
  }

  public setBasicInfo(basicInfo: SubscriberBasicInfo): void {
    this._basicInfo = basicInfo;
    this._createDependentDelegates();
  }

  /** Atomic primary update via `updateSubscriberInfo(subId, info)`. */
  public async updatePrimary(answers: Record<string, string>): Promise<void> {
    const info = {
      ...this._basicInfo.primary,
      ...answers,
    };
    const updated = await updateSubscriberInfo(this._basicInfo.id, info);
    runInAction(() => {
      this.setBasicInfo(mapSubscriberToBasicInfo(updated));
    });
  }

  public async addDependent(dependent: SubscriberPersonInfo): Promise<void> {
    const result = await addOrUpdateDependent(this._basicInfo.id, { ...dependent, id: '' } as PersonInfo);
    const newId = result.data?.addOrUpdateDependent ?? '';

    runInAction(() => {
      this.setBasicInfo({
        ...this._basicInfo,
        dependents: sortDependents([...this._basicInfo.dependents, { ...dependent, id: newId }]),
      });
    });
  }

  public async updateDependent(dependent: SubscriberPersonInfo): Promise<void> {
    const currentDependentDelegate = this._getDependentDelegate(dependent.id);
    if (currentDependentDelegate) {
      await currentDependentDelegate.updateDependent(dependent);
      runInAction(() => {
        this._basicInfo = {
          ...this._basicInfo,
          dependents: this._basicInfo.dependents.map((item) => (item.id === dependent.id ? { ...dependent } : item)),
        };
      });
    }
  }

  public async removeDependent(dependentId: string): Promise<void> {
    await removeSubscriberDependent(this._basicInfo.id, dependentId);
    runInAction(() => {
      this.setBasicInfo({
        ...this._basicInfo,
        dependents: this._basicInfo.dependents.filter((dependent) => dependent.id !== dependentId),
      });
    });
  }

  constructor({ basicInfo, periodId }: SubscriberBasicInfoDelegateParams) {
    this._periodId = periodId;
    this._basicInfo = basicInfo;
    this._createDependentDelegates();

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
